import { lazy } from 'react'
import { AccountTypeGuard } from '@/components/auth/AccountTypeGuard'
import { SubscriptionFeatureGuard } from '@/components/auth/SubscriptionFeatureGuard'
import { OrgRouteGuard } from '@/components/organization/OrgRouteGuard'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import {
  AccountType,
  IntegrationType,
  isIntegrationVisible,
} from '@scrambl/shared'

import { ProtectedLayout } from '../components'
import { AppRouteObject } from '../types'

// Lazy-loaded admin pages — keeps initial bundle out of non-admin sessions
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/integrations'))
const QuickBooksIntegrationPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks'),
)
const QuickBooksIntegrationErrorPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-error'),
)
const QuickBooksIntegrationSuccessPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-success'),
)
const AdminMembersPage = lazy(() => import('@/pages/admin/members'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings'))
const AdminSubscriptionPage = lazy(() => import('@/pages/admin/subscription'))
const AdminSubscriptionPlansPage = lazy(
  () => import('@/pages/admin/subscription/plans'),
)
const AdminSubscriptionSetupSuccessPage = lazy(
  () => import('@/pages/admin/subscription/setup-success'),
)
const AdminSubscriptionSuccessPage = lazy(
  () => import('@/pages/admin/subscription/success'),
)

// Admin placeholder for logs
const AdminLogs = () => (
  <div className="p-8">
    <h1 className="mb-4 text-2xl font-bold">Organization Logs</h1>
    <p>Organization system logs will be displayed here.</p>
  </div>
)

export function LegacyAdminTemplatesRedirect({
  destination = 'statuses',
}: {
  destination?: 'statuses' | 'project-templates'
}) {
  const { orgSlug } = useParams()
  const target =
    destination === 'project-templates'
      ? `/${orgSlug}/projects/templates`
      : `/${orgSlug}/projects/settings?section=statuses`

  return <Navigate to={target} replace />
}

// Admin routes configuration
export const adminRoutes: AppRouteObject[] = [
  {
    path: '/:orgSlug/admin',
    element: <ProtectedLayout />,
    children: [
      {
        element: <OrgRouteGuard />,
        children: [
          // Default redirect to settings
          {
            index: true,
            element: <Navigate to="settings" replace />,
          },
          // Legacy admin dashboard route
          {
            path: 'dashboard',
            element: <Navigate to="../settings" replace />,
            title: 'Admin Settings',
          },
          // Admin members
          {
            path: 'members',
            element: <AdminMembersPage />,
            title: 'Members',
          },
          // Admin permissions
          {
            path: 'permissions',
            element: (
              <SubscriptionFeatureGuard featureKey="custom_permission_sets">
                <AdminMembersPage />
              </SubscriptionFeatureGuard>
            ),
            title: 'Permissions',
          },
          // Admin external organizations
          {
            path: 'external-organizations',
            element: <AdminMembersPage />,
            title: 'External Organizations',
          },
          // Admin settings
          {
            path: 'settings',
            element: <AdminSettingsPage />,
            title: 'Admin Settings',
          },
          // Status configuration now lives in Project Settings. Keep redirects
          // for old Help Center links and saved bookmarks, but do not render
          // the retired admin templates experience.
          {
            path: 'templates',
            children: [
              {
                index: true,
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project Settings',
              },
              {
                path: 'status-templates',
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project Settings',
              },
              {
                path: 'project-templates',
                element: (
                  <LegacyAdminTemplatesRedirect destination="project-templates" />
                ),
                title: 'Project Templates',
              },
            ],
          },
          // Admin integrations (requires subscription feature)
          {
            path: 'integrations',
            element: (
              <SubscriptionFeatureGuard featureKey="integrations">
                <AdminIntegrationsPage />
              </SubscriptionFeatureGuard>
            ),
            title: 'Integrations',
          },
          {
            path: 'integrations/quickbooks',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Settings',
          },
          {
            path: 'integrations/quickbooks/success',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationSuccessPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Connected',
          },
          {
            path: 'integrations/quickbooks/error',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationErrorPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Error',
          },
          {
            path: 'integrations/mcp',
            element: (
              <Navigate to="../../../user-settings#integrations" replace />
            ),
            title: 'MCP Integration',
          },
          // Legacy /admin/time/* → /timesheets/* (grace period: ≥ 2026-10-21)
          {
            path: 'time',
            children: [
              {
                index: true,
                element: <Navigate to="../../timesheets" replace />,
              },
              {
                path: 'pending',
                element: <Navigate to="../../../timesheets" replace />,
              },
              {
                path: 'team',
                element: <Navigate to="../../../timesheets" replace />,
              },
              {
                path: 'settings',
                element: <Navigate to="../../../timesheets/settings" replace />,
              },
            ],
          },
          // Admin subscription & billing — personal accounts are free by
          // construction (no SUBSCRIPTION flag row), so block the whole billing
          // surface by account type rather than relying on a feature flag.
          {
            path: 'subscription',
            element: (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                <Outlet />
              </AccountTypeGuard>
            ),
            children: [
              {
                index: true,
                element: <AdminSubscriptionPage />,
                title: 'Subscription & Billing',
              },
              {
                path: 'usage',
                element: <AdminSubscriptionPage />,
                title: 'Subscription Usage',
              },
              {
                path: 'plans',
                element: <AdminSubscriptionPlansPage />,
                title: 'Subscription Plans',
              },
              {
                path: 'success',
                element: <AdminSubscriptionSuccessPage />,
                title: 'Payment Success',
              },
              {
                path: 'setup-success',
                element: <AdminSubscriptionSetupSuccessPage />,
                title: 'Setup Success',
              },
            ],
          },
          // Admin logs
          {
            path: 'logs',
            element: <AdminLogs />,
            title: 'System Logs',
          },
        ],
      },
    ],
  },
]
